import LaycanEditor from '../grid/editors/LaycanEditor';
import LaycanFormatter from '../grid/formatters/LaycanFormatter';
import LaycanKeyCreator from '../grid/keyCreators/LaycanKeyCreator';
import {
    LaycanComparator,
    LaycanFromComparator,
} from '../grid/comparators/LaycanComparator';
import LaycanFilterComparator from '../grid/comparators/LaycanFilterComparator';
import LaycanFromFormatter from '../grid/formatters/LaycanFromFormatter';
import LaycanValueGetter from '../grid/valueGetters/LaycanValueGetter';
import LaycanMonthValueGetter from '../grid/valueGetters/LaycanMonthValueGetter';
import LaycanDecadeValueGetter from '../grid/valueGetters/LaycanDecadeValueGetter';
import LaycanYearValueGetter from '../grid/valueGetters/LaycanYearValueGetter';

import DefaultDateFormatter from '../grid/formatters/DefaultDateFormatter';
import DateComparator from '../grid/comparators/DateComparator';
import DateEditor from '../grid/editors/DateEditor';

import MultipleFreightPortBasisFormatter from '../grid/formatters/MultipleFreightPortBasisFormatter';
import FreightPortBasisEditor from '../grid/editors/FreightPortBasisEditor';
import MultiFreightPortBasisComparator from '../grid/comparators/MultiFreightPortBasisComparator';
import FreightPortBasisKeyCreator from '../grid/keyCreators/FreightPortBasisKeyCreator';

import { ratesHeading } from '_legacy/modules/columns/rate';
import { cargoQuantityPartsHeading } from '_legacy/modules/columns/quantity';
import { durationPartsHeading } from '_legacy/modules/columns/duration';
import { clarksonsBrokerHeadingForFixtures } from '_legacy/modules/columns/clarksonsBroker';
import { updatedByHeading } from '_legacy/modules/columns/updatedBy';
import {
    vesselNameHeading,
    vesselImoHeading,
    vesselEniHeading,
    vesselYobHeading,
    vesselBuiltDateHeading,
    vesselCategoryHeading,
    vesselDwtHeading,
    vesselScrubberHeading,
    vesselYardHeading,
    vesselDesignHeading,
    vesselImoClassHeading,
    vesselIceClassNotationHeading,
    vesselNitrogenPlantHeading,
    vesselSegsHeading,
} from '_legacy/modules/columns/vessel';
import { thirdPartyOwnerHeading } from '_legacy/modules/columns/thirdPartyOwner';
import { thirdPartyChartererHeading } from '_legacy/modules/columns/thirdPatyCharterer';

import CargoEditor from '../grid/editors/CargoEditor';
import MultipleCargoFormatter from '../grid/formatters/MultipleCargoFormatter';
import CargoPartsComparator from '../grid/comparators/CargoPartsComparator';
import CargoPartsKeyCreator from '../grid/keyCreators/CargoPartsKeyCreator';
import NumericEditor from '../grid/editors/NumericEditor';
import MultipleCompanyFormatter from '../grid/formatters/MultipleCompanyFormatter';
import CompanyFormatter from '_legacy/modules/shared/columns/formatters/CompanyFormatter';
import CompanyKeyCreator from '_legacy/modules/shared/columns/keyCreators/CompanyKeyCreator';
import ChartererEditor from '../grid/editors/ChartererEditor';
import OwnerEditor from '../grid/editors/OwnerEditor';
import BrokerEditor from '../grid/editors/BrokerEditor';
import NamePropertyComparator from '../grid/comparators/NamePropertyComparator';
import suppressOnEdit from '../grid/columnDefaults/suppressOnEdit';
import allowUpAndDownOnEdit from '../grid/columnDefaults/allowUpAndDownOnEdit';
import defaultObjEquals from '../grid/columnDefaults/defaultObjEquals';

import LocationEditor from '../grid/editors/LocationEditor';
import LocationsFormatter from '../../components/grid/formatters/LocationsFormatter';
import LocationPartsKeyCreator from '../grid/keyCreators/LocationPartsKeyCreator';
import LocationPartsComparator from '../grid/comparators/locationPartsComparator';

import TypeFormatter, {
    AllTypeFormatter,
} from '../grid/formatters/TypeFormatter';

import {
    StatusFilterFormatter,
    StatusFormatter,
} from '../grid/formatters/StatusFormatter';
import {
    FetchMappingSelectStatusDisplayValue,
    statusesByDataset,
} from '../../data/mappings';
import StatusComparator from '../grid/comparators/StatusComparator';
import StatusRenderer from '../grid/renderers/StatusRenderer';

import {
    TrackingFormatter,
    TrackingFormatterSelectDisplayValue,
} from '../grid/formatters/TrackingFormatter';

import BrokersComparator from '../grid/comparators/BrokersComparator';
import BrokersKeyCreator from '../grid/keyCreators/BrokersKeyCreator';

import ZoneEditor from '../grid/editors/ZoneEditor';
import ZoneKeyCreator from '../grid/keyCreators/ZoneKeyCreator';
import LoadZoneKeyCreator from '../grid/keyCreators/LoadZoneKeyCreator';

import ZoneComparator from '../grid/comparators/ZoneComparator';

import {
    commonRowAndCellClassRules,
    statusClassRules,
} from '../grid/classRules/classRules';

import BlanksFilterRenderer from '../grid/renderers/BlanksFilterRenderer';
import UnknownFilterRenderer from '../grid/renderers/UnknownFilterRenderer';
import TooltipRenderer from '../grid/renderers/TooltipRenderer';
import GroupNameRenderer from '../grid/renderers/GroupNameRenderer';

import naturalSortComparator from '../grid/comparators/NaturalSortComparator';
import defaultSetFilterParams from '../grid/columnDefaults/defaultSetFilterParams';

import SelectEditor from '_legacy/modules/shared/columns/editors/SelectEditor';
import VisibilityRenderer from '../grid/renderers/VisibilityRenderer';

import VoyageActivityRenderer from '../grid/renderers/VoyageActivityRenderer';

import QuotedFormatter, {
    QuotedFieldFormatter,
} from '../grid/formatters/QuotedFormatter';
import QuotedKeyCreator from '../grid/keyCreators/QuotedKeyCreator';
import { areasByDataset, getAreasGeared } from '../../models/Areas';
import {
    blankKeyText,
    unassignedChartererText,
    unassignedAreaText,
    unassignedAreaGearedText,
    unassignedDirectionText,
} from '../../constants/gridText';
import voyageActivityComparator from '../grid/comparators/voyageActivityComparator';

import CommissionEditor from '../grid/editors/CommissionEditor';
import ParcelsEditor from '../grid/editors/ParcelsEditor';

import ThirdPartyBrokerEditor from '../grid/editors/ThirdPartyBrokerEditor';
import MultipleThirdPartyCompanyFormatter from '../grid/formatters/MultipleThirdPartyCompanyFormatter';

import DirectionValueGetter from '../grid/valueGetters/DirectionValueGetter';

import { channelValues } from '_legacy/constants/channelValues';

import {
    SEPARATOR_FORWARD_SLASH,
    SEPARATOR_PLUS,
} from '../../models/common/EntityPart';
import CommissionFormatter from '../grid/formatters/CommissionFormatter';
import CommissionComparator from '../grid/comparators/CommissionComparator';
import CommissionKeyCreator from '../grid/keyCreators/CommissionKeyCreator';
import { getDataset } from '_legacy/models/Datasets';
import { fixtureDeliveryTermHeading } from '_legacy/modules/columns/deliveryTerm';
import {
    formatSalePrice,
    parseSalePrice,
} from '_legacy/services/SalePriceFormatterService';
import { SalePriceComparator } from '_legacy/modules/columns/quantity/comparators/SalePriceComparator';
import { buyerHeading } from '_legacy/modules/columns/buyer/heading/buyerHeading';
import { operatorHeading } from '_legacy/modules/columns/operator/headings/operatorHeading';
import { opportunityHeading } from '_legacy/modules/columns/opportunity/heading/opportunityHeading';
import { typesByDataset } from '_legacy/models/Type';
import { vesselGasCbmHeading } from '_legacy/modules/columns/vessel/headings/vesselGasCbmHeading';
import WeekComparator from '../grid/comparators/WeekComparator';
import { vesselTankTypeHeading } from '_legacy/modules/columns/vessel/headings/vesselTankTypeHeading';
import { vesselGasTypeHeading } from '_legacy/modules/columns/vessel/headings/vesselGasTypeHeading';
import { fixtureRedeliveryTermHeading } from '_legacy/modules/columns/redeliveryTerm';
import MonthKeyCreator from '../grid/keyCreators/MonthKeyCreator';
import CpDateFormatter from '../grid/formatters/CpDateFormatter';
import PriceEditor from '../grid/editors/PriceEditor';
import { formatAwrp, parseAwrp } from '_legacy/services/AwrpFomatterService';
import { AwrpComparator } from '_legacy/modules/columns/quantity/comparators/AwrpComparator';
import { tceHeading } from '_legacy/modules/columns/tce/TceHeading';
import TypeKeyCreator from '_legacy/modules/shared/columns/keyCreators/TypeKeyCreator';
import chartererTypeHeading from '_legacy/modules/columns/chartererType/chartererTypeHeading';
import ownerTypeHeading from '_legacy/modules/columns/ownerType/ownerTypeHeading';
import nearForwardHeading from '_legacy/modules/columns/nearForward/nearForwardHeading';
import { TypeSortComparator } from '_legacy/modules/shared/columns/comparators/TypeSortComparator';
import { getEastWestHeading } from '_legacy/modules/columns/eastWest/EastWestHeading';
import intraInterRegionalHeading from '_legacy/modules/columns/intraInterRegional/intraInterRegionalHeading';
import { vesselEngineTypeHeading } from '_legacy/modules/columns/vessel/headings/vesselEngineTypeHeading';
import { vesselPropTypeHeading } from '_legacy/modules/columns/vessel/headings/vesselPropTypeHeading';
import { reportedDateHeading } from '_legacy/modules/columns/reportedDate/ReportedDateHeading';
import { getSaleDateHeading } from '_legacy/modules/columns/saleDate/SaleDateHeading';
import { getHireExpiryHeading } from '_legacy/modules/columns/hireExpiry/HireExpiryHeading';
import { updatedTimeHeading } from '_legacy/modules/columns/updatedDateTime/UpdatedDateTimeHeading';
import { createdDateHeading } from '_legacy/modules/columns/createdDate/CreatedDateHeading';
import { laycanToHeading } from '_legacy/modules/columns/laycanTo/LaycanToHeading';
import { getMinRedeliveryHeading } from '_legacy/modules/columns/minRedelivery/MinRedeliveryHeading';
import { getMaxRedeliveryHeading } from '_legacy/modules/columns/maxRedelivery/MaxRedeliveryHeading';
import cleanDirtyCargoHeading from '_legacy/modules/columns/cleanDirtyCargo/cleanDirtyCargoHeading';
import ownerReLetHeading from '_legacy/modules/columns/ownerReLet/ownerReLetHeading';
import vesselDualFuelHeading from '_legacy/modules/columns/vessel/headings/vesselDualFuelHeading';
import { DATASET_TANKERS } from '_legacy/models/Datasets';
import { TankersGroupType } from '_legacy/models/TankersGroupTypes';
import { formatDisplayZones } from 'common/types/GetDisplayZone';

const isEditableRow = (params) => {
    const isEditable =
        params &&
        params.data &&
        params.data.state !== 'deleted' &&
        params.context.canEdit;

    return isEditable;
};

const isEditableNonSpecialisedRow = (params) =>
    isEditableRow(params) && params.context.datasetId !== 13;

const isRedeliveryEditable = (params) => {
    if (!isEditableRow(params)) {
        return false;
    }

    const isTankersDataset = params.context.datasetId === DATASET_TANKERS;
    const isPeriodGroup =
        params.context.tankersGroupType === TankersGroupType.Period;

    if (!isTankersDataset || !isPeriodGroup) {
        return false;
    }

    // Only editable when L/C and Duration are both populated
    const data = params.data;
    const hasLaycan =
        data?.laycan?.shorthand && data.laycan.shorthand.trim() !== '';
    const hasDuration = data?.durationParts && data.durationParts.length > 0;

    return hasLaycan && hasDuration;
};

const saleDateHeading = getSaleDateHeading(isEditableRow);
const hireExpiryHeading = getHireExpiryHeading(isEditableRow);

const checkboxHeading = {
    colId: 'isSelected',
    pinned: 'left',
    headerName: '',
    checkboxSelection: true,
    headerCheckboxSelection: true,
    headerCheckboxSelectionFilteredOnly: true,
    lockPinned: true,
    suppressMenu: true,
    suppressMovable: true,
    suppressColumnsToolPanel: true,
    lockPosition: 'left',
    width: 24,
    minWidth: 35,
    enableCellChangeFlash: false,
};

const privacyHeading = {
    colId: 'privacy',
    headerName: '',
    headerValueGetter: (params) =>
        params.location === 'columnToolPanel' ? 'Visibility' : '',
    columnToolPanelLabel: 'Visibility',
    field: 'privacy',
    editable: false,
    cellRendererFramework: VisibilityRenderer,
    headerClass: 'grid-heading-privacy',
    cellClass: 'grid-cell-privacy',
    cellClassRules: {
        ...commonRowAndCellClassRules,
    },
    filter: 'visibilityFilter',
    width: 60,
    minWidth: 60,
};

const statusHeading = {
    colId: 'status',
    headerName: 'Status',
    field: 'status',
    cellEditorFramework: SelectEditor,
    cellEditorParams: {
        valuesByDataset: statusesByDataset,
        formatValue: FetchMappingSelectStatusDisplayValue,
    },
    suppressKeyboardEvent: allowUpAndDownOnEdit(),
    valueFormatter: StatusFormatter,
    cellRendererFramework: StatusRenderer,
    editable: isEditableRow,
    comparator: StatusComparator,
    cellClassRules: {
        ...statusClassRules,
    },
    filterParams: {
        ...defaultSetFilterParams,
        comparator: naturalSortComparator,
        valueFormatter: StatusFilterFormatter,
    },
    tooltipValueGetter: (params) =>
        FetchMappingSelectStatusDisplayValue(
            params.value,
            params.data.datasetId
        ),
    width: 67,
};

export const cpDateHeading = {
    colId: 'cpDate',
    headerName: 'CP Date',
    field: 'cpDate',
    enableRowGroup: true,
    valueFormatter: CpDateFormatter,
    cellEditorFramework: DateEditor,
    editable: isEditableRow,
    equals: defaultObjEquals,
    comparator: DateComparator,
    keyCreator: MonthKeyCreator,
    excelExportFormat: 'date',
    cellClassRules: {
        ...commonRowAndCellClassRules,
    },
    filterParams: {
        ...defaultSetFilterParams,
        comparator: naturalSortComparator,
    },
    filter: 'dateFilter',
    width: 85,
};

const weekHeading = {
    colId: 'week',
    headerName: 'Week',
    field: 'week',
    headerTooltip: 'Read-only, auto-calculated based on CP date',
    editable: false,
    equals: defaultObjEquals,
    comparator: WeekComparator,
    type: ['privatable'],
    cellClassRules: {
        ...commonRowAndCellClassRules,
    },
    filterParams: {
        ...defaultSetFilterParams,
        comparator: naturalSortComparator,
    },
    width: 85,
    hide: false,
};

const minRedelivery = getMinRedeliveryHeading(
    isRedeliveryEditable,
    'Auto-calculated based on L/C and firm Duration. Editable in Tankers Period groups when L/C and Duration are populated.'
);

const maxRedelivery = getMaxRedeliveryHeading(
    isRedeliveryEditable,
    'Auto-calculated based on L/C and firm Duration. Editable in Tankers Period groups when L/C and Duration are populated.'
);

const rateHeading = {
    colId: 'rate',
    headerName: 'Rate',
    field: 'rate',
    editable: isEditableNonSpecialisedRow,
    type: ['privatable', 'rumoured'],
    cellClassRules: {
        ...commonRowAndCellClassRules,
    },
    filterParams: {
        ...defaultSetFilterParams,
        cellRenderer: BlanksFilterRenderer,
        comparator: naturalSortComparator,
    },
    width: 100,
    suppressPaste: false,
};

const laycanHeading = {
    colId: 'laycan',
    headerName: 'L/C',
    field: 'laycan',
    headerTooltip:
        'The date the cargo will be loaded on a VOY or where the vessel is delivered on a TCT',
    editable: isEditableRow,
    cellEditorFramework: LaycanEditor,
    valueFormatter: LaycanFormatter,
    valueGetter: LaycanValueGetter,
    comparator: LaycanComparator,
    equals: defaultObjEquals,
    keyCreator: LaycanKeyCreator,
    filter: 'laycanFilter',
    type: ['privatable', 'rumoured'],
    cellClassRules: {
        ...commonRowAndCellClassRules,
    },
    width: 100,
};

const laycanFromHeading = {
    colId: 'laycanFrom',
    headerName: 'L/C From',
    field: 'laycan',
    editable: false,
    sortable: true,
    suppressMenu: false,
    valueFormatter: LaycanFromFormatter,
    comparator: LaycanFromComparator,
    hide: true,
    cellClassRules: {
        ...commonRowAndCellClassRules,
    },
    filter: 'agDateColumnFilter',
    filterParams: {
        comparator: LaycanFilterComparator,
        suppressAndOrCondition: true,
        inRangeInclusive: true,
        filterOptions: ['equals', 'lessThan', 'greaterThan', 'inRange'],
        defaultOption: 'inRange',
    },
    excelExportFormat: 'date',
    width: 80,
};

const laycanYearHeading = {
    colId: 'laycanYear',
    editable: false,
    headerName: 'Year',
    field: 'laycan',
    valueGetter: LaycanYearValueGetter,
    hide: true,
    cellClassRules: {
        ...commonRowAndCellClassRules,
    },
    suppressMenu: true,
    filter: false,
    width: 80,
};

const laycanMonthHeading = {
    colId: 'laycanMonth',
    editable: false,
    headerName: 'Month',
    field: 'laycan',
    valueGetter: LaycanMonthValueGetter,
    hide: true,
    cellClassRules: {
        ...commonRowAndCellClassRules,
    },
    suppressMenu: true,
    filter: false,
    width: 80,
};

const laycanDecadeHeading = {
    colId: 'laycanDecade',
    editable: false,
    headerName: 'Decade',
    field: 'laycan',
    valueGetter: LaycanDecadeValueGetter,
    hide: true,
    cellClassRules: {
        ...commonRowAndCellClassRules,
    },
    suppressMenu: true,
    filter: false,
    width: 80,
};

const commentsHeading = {
    colId: 'comments',
    headerName: 'Comments',
    field: 'comments',
    editable: isEditableRow,
    type: ['privatable'],
    cellClassRules: {
        ...commonRowAndCellClassRules,
    },
    cellRenderer: TooltipRenderer,
    filterParams: {
        ...defaultSetFilterParams,
        cellRenderer: BlanksFilterRenderer,
        comparator: naturalSortComparator,
    },
    width: 120,
    suppressPaste: false,
};

const dischargeLocationHeading = {
    colId: 'dischargeLocationParts',
    headerName: 'Disch',
    field: 'dischargeLocationParts',
    editable: isEditableRow,
    cellEditorFramework: LocationEditor,
    cellEditorParams: {
        defaultSeperatorCharacter: SEPARATOR_FORWARD_SLASH,
        blockedCharacters: [],
    },
    cellRenderer: (location) =>
        TooltipRenderer({ value: location.valueFormatted }),
    valueFormatter: LocationsFormatter,
    equals: defaultObjEquals,
    suppressKeyboardEvent: suppressOnEdit(),
    comparator: LocationPartsComparator,
    keyCreator: LocationPartsKeyCreator,
    filter: 'locationsFilter',
    type: ['privatable', 'rumoured'],
    cellClassRules: {
        ...commonRowAndCellClassRules,
    },
    filterParams: {
        ...defaultSetFilterParams,
        comparator: naturalSortComparator,
    },
    width: 120,
};

const redeliveryLocationHeading = {
    colId: 'redeliveryLocationParts',
    headerName: 'Redelivery',
    field: 'redeliveryLocationParts',
    editable: isEditableRow,
    cellEditorFramework: LocationEditor,
    cellEditorParams: {
        defaultSeperatorCharacter: SEPARATOR_FORWARD_SLASH,
        blockedCharacters: [],
    },
    cellRenderer: (location) =>
        TooltipRenderer({ value: location.valueFormatted }),
    valueFormatter: LocationsFormatter,
    equals: defaultObjEquals,
    suppressKeyboardEvent: suppressOnEdit(),
    comparator: LocationPartsComparator,
    keyCreator: LocationPartsKeyCreator,
    filter: 'locationsFilter',
    type: ['privatable', 'rumoured'],
    cellClassRules: {
        ...commonRowAndCellClassRules,
    },
    filterParams: {
        ...defaultSetFilterParams,
        comparator: naturalSortComparator,
    },
    width: 120,
};

const cargoPartsHeading = {
    colId: 'cargoParts',
    headerName: 'Cgo',
    field: 'cargoParts',
    enableRowGroup: true,
    editable: isEditableRow,
    valueFormatter: MultipleCargoFormatter,
    cellEditorFramework: CargoEditor,
    cellRenderer: (cargo) => TooltipRenderer({ value: cargo.valueFormatted }),
    suppressKeyboardEvent: suppressOnEdit(),
    comparator: CargoPartsComparator,
    equals: defaultObjEquals,
    keyCreator: CargoPartsKeyCreator,
    filter: 'cargoesFilter',
    width: 120,
    type: ['privatable', 'rumoured'],
    cellClassRules: {
        ...commonRowAndCellClassRules,
    },
    filterParams: {
        ...defaultSetFilterParams,
        cellRenderer: BlanksFilterRenderer,
        comparator: naturalSortComparator,
    },
};

const freightPortBasisHeading = {
    colId: 'freightPortBasis',
    headerName: 'Freight Port Basis',
    field: 'freightPortBasis',
    editable: isEditableRow,
    valueFormatter: MultipleFreightPortBasisFormatter,
    cellEditorFramework: FreightPortBasisEditor,
    cellRenderer: (port) => TooltipRenderer({ value: port.valueFormatted }),
    suppressKeyboardEvent: suppressOnEdit(),
    comparator: MultiFreightPortBasisComparator,
    equals: defaultObjEquals,
    keyCreator: FreightPortBasisKeyCreator,
    width: 120,
    type: ['privatable', 'rumoured'],
    cellClassRules: {
        ...commonRowAndCellClassRules,
    },
    filterParams: {
        ...defaultSetFilterParams,
        comparator: naturalSortComparator,
    },
    hide: true,
};

const loadLocationHeading = {
    colId: 'loadLocationParts',
    headerName: 'Load',
    field: 'loadLocationParts',
    editable: isEditableRow,
    cellEditorFramework: LocationEditor,
    cellEditorParams: {
        defaultSeperatorCharacter: SEPARATOR_FORWARD_SLASH,
        blockedCharacters: [],
    },
    cellRenderer: (location) =>
        TooltipRenderer({ value: location.valueFormatted }),
    valueFormatter: LocationsFormatter,
    equals: defaultObjEquals,
    suppressKeyboardEvent: suppressOnEdit(),
    comparator: LocationPartsComparator,
    keyCreator: LocationPartsKeyCreator,
    filter: 'locationsFilter',
    type: ['privatable', 'rumoured'],
    cellClassRules: {
        ...commonRowAndCellClassRules,
    },
    filterParams: {
        ...defaultSetFilterParams,
        comparator: naturalSortComparator,
    },
    width: 100,
};

const deliveryLocationHeading = {
    colId: 'deliveryLocationParts',
    headerName: 'Delivery',
    field: 'deliveryLocationParts',
    editable: isEditableRow,
    cellEditorFramework: LocationEditor,
    cellEditorParams: {
        defaultSeperatorCharacter: SEPARATOR_FORWARD_SLASH,
        blockedCharacters: [],
    },
    cellRenderer: (location) =>
        TooltipRenderer({ value: location.valueFormatted }),
    valueFormatter: LocationsFormatter,
    equals: defaultObjEquals,
    suppressKeyboardEvent: suppressOnEdit(),
    comparator: LocationPartsComparator,
    keyCreator: LocationPartsKeyCreator,
    filter: 'locationsFilter',
    type: ['privatable', 'rumoured'],
    cellClassRules: {
        ...commonRowAndCellClassRules,
    },
    filterParams: {
        ...defaultSetFilterParams,
        comparator: naturalSortComparator,
    },
    width: 100,
};

const loadLocationZoneHeading = {
    colId: 'loadZones',
    headerName: 'Load Z',
    field: 'loadZones',
    enableRowGroup: true,
    editable: isEditableNonSpecialisedRow,
    cellEditorSelector: (params) => ({
        component:
            params.context.datasetId === DATASET_TANKERS
                ? 'multiZoneEditor'
                : 'zoneEditor',
    }),
    suppressKeyboardEvent: suppressOnEdit(),
    keyCreator: LoadZoneKeyCreator,
    comparator: (value1, value2, node1, node2) =>
        ZoneComparator(value1, value2, node1, node2, 'loadZone'),
    valueFormatter: (params) =>
        formatDisplayZones(params.value, params.data?.loadZone, 'loadZones'),
    filter: 'zonesFilter',
    type: ['privatable'],
    cellClassRules: {
        ...commonRowAndCellClassRules,
    },
    filterParams: {
        ...defaultSetFilterParams,
        cellRenderer: BlanksFilterRenderer,
        comparator: naturalSortComparator,
    },
    width: 100,
};

const dischargeLocationZoneHeading = {
    colId: 'dischargeZones',
    headerName: 'Disch Z',
    field: 'dischargeZones',
    editable: isEditableNonSpecialisedRow,
    cellEditorSelector: (params) => ({
        component:
            params.context.datasetId === DATASET_TANKERS
                ? 'multiZoneEditor'
                : 'zoneEditor',
    }),
    suppressKeyboardEvent: suppressOnEdit(),
    keyCreator: (params) => ZoneKeyCreator(params, 'dischargeZone'),
    comparator: (value1, value2, node1, node2) =>
        ZoneComparator(value1, value2, node1, node2, 'dischargeZone'),
    valueFormatter: (params) =>
        formatDisplayZones(
            params.value,
            params.data?.dischargeZone,
            'dischargeZones'
        ),
    filter: 'zonesFilter',
    type: ['privatable'],
    cellClassRules: {
        ...commonRowAndCellClassRules,
    },
    filterParams: {
        ...defaultSetFilterParams,
        cellRenderer: BlanksFilterRenderer,
        comparator: naturalSortComparator,
    },
    width: 100,
};

const areaHeading = {
    colId: 'areas',
    headerValueGetter: (params) =>
        getDataset(params.context.datasetId).headingsMapping['areas'],
    field: 'areas',
    enableRowGroup: true,
    editable: isEditableNonSpecialisedRow,
    sortable: true,
    keyCreator: ({ value }) => {
        if (!value) {
            return unassignedAreaText;
        }
        return value;
    },
    cellEditorFramework: SelectEditor,
    cellEditorParams: {
        valuesByDataset: areasByDataset,
    },
    suppressKeyboardEvent: allowUpAndDownOnEdit(),
    cellClassRules: {
        ...commonRowAndCellClassRules,
    },
    filterParams: {
        ...defaultSetFilterParams,
        cellRenderer: BlanksFilterRenderer,
        comparator: naturalSortComparator,
    },
    hide: true,
    width: 100,
};

const areaGearedHeading = {
    colId: 'areasGeared',
    headerName: 'Area Geared',
    field: 'areasGeared',
    editable: isEditableRow,
    enableRowGroup: true,
    sortable: true,
    keyCreator: ({ value }) => {
        if (!value) {
            return unassignedAreaGearedText;
        }
        return value;
    },
    cellEditorFramework: SelectEditor,
    cellEditorParams: {
        valuesByDataset: getAreasGeared,
    },
    suppressKeyboardEvent: allowUpAndDownOnEdit(),
    cellClassRules: {
        ...commonRowAndCellClassRules,
    },
    filterParams: {
        ...defaultSetFilterParams,
        cellRenderer: BlanksFilterRenderer,
        comparator: naturalSortComparator,
    },
    hide: true,
    width: 100,
};

const chartererHeading = {
    colId: 'charterer',
    headerName: 'Charterer',
    field: 'charterer',
    enableRowGroup: true,
    editable: isEditableRow,
    valueFormatter: CompanyFormatter,
    cellEditorFramework: ChartererEditor,
    suppressKeyboardEvent: suppressOnEdit(),
    comparator: NamePropertyComparator,
    equals: defaultObjEquals,
    keyCreator: (params) =>
        CompanyKeyCreator({ ...params, defaultValue: unassignedChartererText }),
    type: ['privatable', 'rumoured'],
    cellClassRules: {
        ...commonRowAndCellClassRules,
    },
    filterParams: {
        ...defaultSetFilterParams,
        comparator: naturalSortComparator,
        cellRenderer: BlanksFilterRenderer,
    },
    width: 120,
};

const thirdPartyBrokersHeading = {
    colId: 'thirdPartyBrokers',
    headerName: 'Broker',
    field: 'thirdPartyBrokers',
    editable: isEditableRow,
    valueFormatter: MultipleThirdPartyCompanyFormatter,
    cellEditorFramework: ThirdPartyBrokerEditor,
    cellEditorParams: {
        blockedCharacters: [SEPARATOR_FORWARD_SLASH],
        seperatorCharacters: [SEPARATOR_PLUS],
    },
    cellRenderer: (broker) => TooltipRenderer({ value: broker.valueFormatted }),
    suppressKeyboardEvent: suppressOnEdit(),
    comparator: BrokersComparator,
    equals: defaultObjEquals,
    keyCreator: BrokersKeyCreator,
    width: 120,
    filter: 'companiesFilter',
    type: ['privatable', 'rumoured'],
    cellClassRules: {
        ...commonRowAndCellClassRules,
    },
    filterParams: {
        ...defaultSetFilterParams,
        cellRenderer: BlanksFilterRenderer,
        comparator: naturalSortComparator,
    },
};

const demurrageHeading = {
    colId: 'demurrage',
    headerName: 'Dem (K)',
    field: 'demurrage',
    editable: isEditableRow,
    cellEditorFramework: NumericEditor,
    type: ['privatable'],
    cellClassRules: {
        ...commonRowAndCellClassRules,
    },
    filterParams: {
        ...defaultSetFilterParams,
        cellRenderer: BlanksFilterRenderer,
        comparator: naturalSortComparator,
    },
    width: 65,
};

const brokerHeading = {
    colId: 'brokers',
    headerName: 'Broker',
    field: 'brokers',
    editable: isEditableRow,
    valueFormatter: MultipleCompanyFormatter,
    cellEditorFramework: BrokerEditor,
    suppressKeyboardEvent: suppressOnEdit(),
    comparator: BrokersComparator,
    equals: defaultObjEquals,
    keyCreator: BrokersKeyCreator,
    type: ['privatable', 'rumoured'],
    cellClassRules: {
        ...commonRowAndCellClassRules,
    },
    filterParams: {
        ...defaultSetFilterParams,
        cellRenderer: BlanksFilterRenderer,
        comparator: naturalSortComparator,
    },
    cellEditorParams: { requireConfirmation: true },
    width: 120,
};

const typeHeading = {
    colId: 'type',
    headerName: 'Type',
    field: 'type',
    editable: isEditableRow,
    valueFormatter: TypeFormatter,
    cellEditorFramework: SelectEditor,
    cellEditorParams: {
        valuesByDataset: typesByDataset,
        formatValue: AllTypeFormatter,
    },
    suppressKeyboardEvent: allowUpAndDownOnEdit(),
    cellClassRules: {
        ...commonRowAndCellClassRules,
    },
    filterParams: {
        ...defaultSetFilterParams,
        cellRenderer: UnknownFilterRenderer,
        comparator: naturalSortComparator,
    },
    width: 67,
    keyCreator: TypeKeyCreator,
    comparator: TypeSortComparator,
};

const ownerHeading = {
    colId: 'owner',
    headerName: 'Owner',
    field: 'owner',
    editable: isEditableRow,
    valueFormatter: CompanyFormatter,
    cellEditorFramework: OwnerEditor,
    suppressKeyboardEvent: suppressOnEdit(),
    comparator: NamePropertyComparator,
    equals: defaultObjEquals,
    keyCreator: CompanyKeyCreator,
    type: ['privatable', 'rumoured'],
    cellClassRules: {
        ...commonRowAndCellClassRules,
    },
    filterParams: {
        ...defaultSetFilterParams,
        comparator: naturalSortComparator,
        cellRenderer: BlanksFilterRenderer,
    },
    width: 120,
};

const actualDirectionHeading = {
    colId: 'actualDirection',
    headerName: 'E-W',
    field: 'actualDirection',
    editable: isEditableRow,
    cellEditorFramework: SelectEditor,
    cellEditorParams: {
        values: ['', 'E', 'W'],
    },
    suppressKeyboardEvent: allowUpAndDownOnEdit(),
    cellClassRules: {
        ...commonRowAndCellClassRules,
    },
    filterParams: {
        ...defaultSetFilterParams,
        cellRenderer: BlanksFilterRenderer,
        comparator: naturalSortComparator,
    },
    hide: true,
    width: 67,
};

const directionHeading = {
    colId: 'direction',
    headerName: 'Direction',
    field: 'direction',
    editable: false,
    enableRowGroup: true,
    type: ['privatable'],
    valueGetter: DirectionValueGetter,
    keyCreator: ({ value }) => {
        if (!value) {
            return unassignedDirectionText;
        }
        return value;
    },
    cellClassRules: {
        ...commonRowAndCellClassRules,
    },
    filterParams: {
        ...defaultSetFilterParams,
        cellRenderer: BlanksFilterRenderer,
        comparator: naturalSortComparator,
    },
    hide: true,
    width: 100,
};

const channelHeading = {
    colId: 'channel',
    headerName: 'Channel',
    field: 'channel',
    editable: isEditableRow,
    cellEditorFramework: SelectEditor,
    cellEditorParams: {
        values: channelValues,
    },
    suppressKeyboardEvent: allowUpAndDownOnEdit(),
    type: ['privatable', 'rumoured'],
    cellClassRules: {
        ...commonRowAndCellClassRules,
    },
    filterParams: {
        ...defaultSetFilterParams,
        cellRenderer: BlanksFilterRenderer,
        comparator: naturalSortComparator,
    },
    hide: false,
    width: 125,
};

const trackingHeading = {
    colId: 'tracking',
    headerName: 'Tracking',
    field: 'tracking',
    editable: isEditableRow,
    cellEditorFramework: SelectEditor,
    valueFormatter: TrackingFormatter,
    keyCreator: ({ value }) => (value && value.length ? value : 'N'),
    cellEditorParams: {
        values: ['N', 'Y', 'COMP'],
        formatValue: TrackingFormatterSelectDisplayValue,
    },
    suppressKeyboardEvent: allowUpAndDownOnEdit(),
    cellClassRules: {
        ...commonRowAndCellClassRules,
    },
    filterParams: {
        ...defaultSetFilterParams,
    },
    hide: true,
    width: 100,
};

const lastOpenLocationHeading = {
    colId: 'lastOpenLocationParts',
    headerName: 'Last Open Location',
    field: 'lastOpenLocationParts',
    editable: isEditableRow,
    cellEditorFramework: LocationEditor,
    suppressKeyboardEvent: suppressOnEdit(),
    keyCreator: LocationPartsKeyCreator,
    valueFormatter: LocationsFormatter,
    equals: defaultObjEquals,
    type: ['privatable'],
    cellClassRules: {
        ...commonRowAndCellClassRules,
    },
    filterParams: {
        ...defaultSetFilterParams,
        comparator: naturalSortComparator,
    },
    hide: true,
    width: 120,
};

const etaHeading = {
    colId: 'eta',
    headerName: 'ETA',
    field: 'eta',
    valueFormatter: DefaultDateFormatter,
    cellEditorFramework: DateEditor,
    editable: isEditableRow,
    equals: defaultObjEquals,
    comparator: DateComparator,
    type: ['privatable'],
    excelExportFormat: 'date',
    cellClassRules: {
        ...commonRowAndCellClassRules,
    },
    filter: 'dateFilter',
    hide: true,
    width: 85,
};

const lastOpenDateHeading = {
    colId: 'lastOpenDate',
    headerName: 'Last Open Date',
    field: 'lastOpenDate',
    valueFormatter: DefaultDateFormatter,
    cellEditorFramework: DateEditor,
    editable: isEditableRow,
    equals: defaultObjEquals,
    comparator: DateComparator,
    type: ['privatable'],
    excelExportFormat: 'date',
    cellClassRules: {
        ...commonRowAndCellClassRules,
    },
    filter: 'dateFilter',
    hide: true,
    width: 85,
};

const salePriceHeading = {
    colId: 'salePrice',
    headerName: 'Sale Price',
    field: 'salePrice',
    editable: true,
    hide: true,
    cellEditorFramework: PriceEditor,
    cellEditorParams: {
        valueParser: (value) => parseSalePrice(value),
        valueFormatter: (value) => formatSalePrice(value),
    },
    sortable: true,
    type: ['privatable'],
    width: 150,
    valueFormatter: (params) => formatSalePrice(params.value),
    comparator: SalePriceComparator,
    cellClassRules: {
        ...commonRowAndCellClassRules,
    },
    filter: 'agMultiColumnFilter',
    filterParams: {
        filters: [
            {
                filter: 'priceFilter',
                filterParams: {
                    ...defaultSetFilterParams,
                    valueParser: (value) => parseSalePrice(value),
                },
            },
            {
                filter: 'agSetColumnFilter',
                filterParams: {
                    ...defaultSetFilterParams,
                    comparator: SalePriceComparator,
                    refreshValuesOnOpen: true,
                    suppressRemoveEntries: true,
                    cellRenderer: BlanksFilterRenderer,
                    valueFormatter: (params) =>
                        formatSalePrice(params.value, true),
                },
            },
        ],
    },
};

// We need this header in place so that we can use setDataValue() methods for the privateFields (cell privacy)
// column. Otherwise, it's ignored. It is not displayed and cannot be picked from column selector.
//
const privateFieldsHeading = {
    colId: 'privateFields',
    headerName: 'Private Fields',
    field: 'privateFields',
    editable: false,
    hide: true,
    suppressColumnsToolPanel: true,
};

// Same to private fields
const rumouredFieldsHeading = {
    colId: 'rumouredFields',
    headerName: 'Rumoured Fields',
    field: 'rumouredFields',
    editable: false,
    hide: true,
    suppressColumnsToolPanel: true,
};

const groupName = {
    colId: 'groupName',
    headerName: 'Group',
    field: 'groupName',
    editable: false,
    enableRowGroup: true,
    cellRendererFramework: GroupNameRenderer,
    filterParams: {
        ...defaultSetFilterParams,
        comparator: naturalSortComparator,
    },
    cellClassRules: {
        ...commonRowAndCellClassRules,
    },
};

const rumouredHeading = {
    colId: 'rumoured',
    headerName: 'Rumoured',
    field: 'rumoured',
    editable: false,
    hide: true,
    suppressColumnsToolPanel: true,
};

const privateCommentsHeading = {
    colId: 'privateComments',
    headerName: 'Private Comments',
    field: 'privateComments',
    editable: isEditableRow,
    cellRenderer: TooltipRenderer,
    filterParams: {
        ...defaultSetFilterParams,
        cellRenderer: BlanksFilterRenderer,
        comparator: naturalSortComparator,
    },
    cellClassRules: {
        ...commonRowAndCellClassRules,
    },
    hide: true,
    suppressPaste: false,
};

const actualLoadHeading = {
    colId: 'actualLoad',
    headerName: 'Actual Load',
    field: 'actualLoad',
    editable: isEditableRow,
    cellEditorFramework: LocationEditor,
    cellEditorParams: {
        defaultSeperatorCharacter: SEPARATOR_PLUS,
        blockedCharacters: [SEPARATOR_FORWARD_SLASH],
    },
    suppressKeyboardEvent: suppressOnEdit(),
    keyCreator: LocationPartsKeyCreator,
    valueFormatter: LocationsFormatter,
    equals: defaultObjEquals,
    type: ['privatable'],
    cellClassRules: {
        ...commonRowAndCellClassRules,
    },
    filterParams: {
        ...defaultSetFilterParams,
        comparator: naturalSortComparator,
    },
    hide: true,
    width: 120,
};

const actualDischargeHeading = {
    colId: 'actualDischarge',
    headerName: 'Actual Discharge',
    field: 'actualDischarge',
    editable: isEditableRow,
    cellEditorFramework: LocationEditor,
    cellEditorParams: {
        defaultSeperatorCharacter: SEPARATOR_PLUS,
        blockedCharacters: [SEPARATOR_FORWARD_SLASH],
    },
    suppressKeyboardEvent: suppressOnEdit(),
    keyCreator: LocationPartsKeyCreator,
    valueFormatter: LocationsFormatter,
    equals: defaultObjEquals,
    type: ['privatable'],
    cellClassRules: {
        ...commonRowAndCellClassRules,
    },
    filterParams: {
        ...defaultSetFilterParams,
        comparator: naturalSortComparator,
    },
    hide: true,
    width: 120,
};

const voyageActivityHeading = {
    colId: 'voyageActivity',
    headerName: 'Voyage Activity',
    field: 'voyageActivity',
    editable: false,
    type: ['privatable'],
    keyCreator: ({ value }) => (value && value.length ? value : blankKeyText),
    cellClassRules: {
        ...commonRowAndCellClassRules,
    },
    filterParams: {
        ...defaultSetFilterParams,
        comparator: voyageActivityComparator,
    },
    cellRendererFramework: VoyageActivityRenderer,
    hide: true,
    width: 120,
};

const quotedHeading = {
    colId: 'quoted',
    headerName: 'Quoted',
    field: 'quoted',
    enableRowGroup: true,
    editable: isEditableRow,
    valueFormatter: QuotedFormatter,
    cellEditorFramework: SelectEditor,
    keyCreator: QuotedKeyCreator,
    cellEditorParams: {
        values: ['Y', 'N'],
        formatValue: QuotedFieldFormatter,
    },
    suppressKeyboardEvent: allowUpAndDownOnEdit(),
    cellClassRules: {
        ...commonRowAndCellClassRules,
    },
    filterParams: {
        ...defaultSetFilterParams,
    },
    hide: true,
    type: ['privatable'],
};

const offersHeading = {
    colId: 'offers',
    headerName: 'Offers',
    field: 'offers',
    editable: isEditableRow,
    width: 120,
    type: ['privatable', 'rumoured'],
    cellClassRules: {
        ...commonRowAndCellClassRules,
    },
    cellRenderer: TooltipRenderer,
    filterParams: {
        ...defaultSetFilterParams,
        cellRenderer: BlanksFilterRenderer,
        comparator: naturalSortComparator,
    },
    hide: true,
    suppressPaste: false,
};

const commissionHeading = {
    colId: 'commission',
    headerName: 'Commission',
    field: 'commission',
    editable: isEditableRow,
    cellEditorFramework: CommissionEditor,
    type: ['privatable', 'rumoured'],
    cellClassRules: {
        ...commonRowAndCellClassRules,
    },
    cellRenderer: (commission) =>
        TooltipRenderer({ value: commission.valueFormatted }),
    valueFormatter: CommissionFormatter,
    equals: defaultObjEquals,
    keyCreator: CommissionKeyCreator,
    comparator: CommissionComparator,
    filterParams: {
        ...defaultSetFilterParams,
        cellRenderer: BlanksFilterRenderer,
        comparator: naturalSortComparator,
    },
    width: 100,
    hide: true,
};

const parcelsHeading = {
    colId: 'parcels',
    headerName: 'Parcels',
    field: 'parcels',
    editable: isEditableRow,
    cellEditorFramework: ParcelsEditor,
    type: ['privatable'],
    cellClassRules: {
        ...commonRowAndCellClassRules,
    },
    filterParams: {
        ...defaultSetFilterParams,
        cellRenderer: BlanksFilterRenderer,
        comparator: naturalSortComparator,
    },
    width: 100,
    hide: true,
};

const awrpHeading = {
    colId: 'awrp',
    headerName: 'AWRP',
    field: 'awrp',
    editable: true,
    hide: true,
    cellEditorFramework: PriceEditor,
    cellEditorParams: {
        valueParser: (value) => parseAwrp(value),
        valueFormatter: (value) => formatAwrp(value),
    },
    sortable: true,
    type: ['privatable'],
    width: 80,
    valueFormatter: (params) => formatAwrp(params.value),
    comparator: AwrpComparator,
    cellClassRules: {
        ...commonRowAndCellClassRules,
    },
    filter: 'agMultiColumnFilter',
    filterParams: {
        filters: [
            {
                filter: 'priceFilter',
                filterParams: {
                    ...defaultSetFilterParams,
                    valueParser: (value) => parseAwrp(value),
                },
            },
            {
                filter: 'agSetColumnFilter',
                filterParams: {
                    ...defaultSetFilterParams,
                    comparator: AwrpComparator,
                    refreshValuesOnOpen: true,
                    suppressRemoveEntries: true,
                    cellRenderer: BlanksFilterRenderer,
                    valueFormatter: (params) => formatAwrp(params.value, true),
                },
            },
        ],
    },
};

const deliveryZoneHeading = {
    colId: 'deliveryZones',
    headerName: 'Delivery Z',
    field: 'deliveryZones',
    enableRowGroup: false,
    editable: false,
    cellEditorFramework: ZoneEditor,
    suppressKeyboardEvent: suppressOnEdit(),
    comparator: ZoneComparator,
    valueFormatter: (params) =>
        formatDisplayZones(
            params.value,
            params.data?.deliveryZone,
            'deliveryZones'
        ),
    keyCreator: ZoneKeyCreator,
    filter: 'zonesFilter',
    type: ['privatable'],
    cellClassRules: {
        ...commonRowAndCellClassRules,
    },
    filterParams: {
        ...defaultSetFilterParams,
        cellRenderer: BlanksFilterRenderer,
        comparator: naturalSortComparator,
    },
    width: 100,
};

const headers = [
    checkboxHeading,
    privacyHeading,
    groupName,
    reportedDateHeading,
    updatedTimeHeading,
    updatedByHeading,
    vesselNameHeading,
    vesselTankTypeHeading,
    vesselGasCbmHeading,
    statusHeading,
    vesselGasTypeHeading,
    cpDateHeading,
    typeHeading,
    cargoQuantityPartsHeading,
    vesselEngineTypeHeading,
    vesselPropTypeHeading,
    cargoPartsHeading,
    fixtureDeliveryTermHeading,
    deliveryLocationHeading,
    loadLocationHeading,
    dischargeLocationHeading,
    fixtureRedeliveryTermHeading,
    redeliveryLocationHeading,
    durationPartsHeading,
    hireExpiryHeading,
    freightPortBasisHeading,
    nearForwardHeading,
    laycanHeading,
    laycanFromHeading,
    laycanToHeading,
    rateHeading,
    ratesHeading,
    tceHeading,
    chartererHeading,
    thirdPartyChartererHeading,
    chartererTypeHeading,
    thirdPartyOwnerHeading,
    ownerTypeHeading,
    thirdPartyBrokersHeading,
    getEastWestHeading('loadEW', 'Load E/W'),
    intraInterRegionalHeading,
    getEastWestHeading('dischEW', 'Disch E/W'),
    getEastWestHeading('deliveryEW', 'Delivery E/W'),
    getEastWestHeading('redeliveryEW', 'Redelivery E/W'),
    clarksonsBrokerHeadingForFixtures,
    demurrageHeading,
    brokerHeading,
    buyerHeading,
    loadLocationZoneHeading,
    dischargeLocationZoneHeading,
    areaHeading,
    areaGearedHeading,
    ownerHeading,
    privateCommentsHeading,
    commentsHeading,
    trackingHeading,
    actualDirectionHeading,
    directionHeading,
    etaHeading,
    vesselImoHeading,
    vesselEniHeading,
    vesselYobHeading,
    vesselBuiltDateHeading,
    vesselCategoryHeading,
    vesselDwtHeading,
    vesselScrubberHeading,
    vesselYardHeading,
    vesselDesignHeading,
    vesselImoClassHeading,
    vesselIceClassNotationHeading,
    vesselNitrogenPlantHeading,
    vesselSegsHeading,
    lastOpenLocationHeading,
    lastOpenDateHeading,
    salePriceHeading,
    saleDateHeading,
    laycanYearHeading,
    laycanMonthHeading,
    laycanDecadeHeading,
    privateFieldsHeading,
    rumouredFieldsHeading,
    rumouredHeading,
    actualLoadHeading,
    actualDischargeHeading,
    voyageActivityHeading,
    quotedHeading,
    offersHeading,
    opportunityHeading,
    commissionHeading,
    parcelsHeading,
    channelHeading,
    minRedelivery,
    maxRedelivery,
    createdDateHeading,
    operatorHeading,
    weekHeading,
    awrpHeading,
    deliveryZoneHeading,
    cleanDirtyCargoHeading,
    ownerReLetHeading,
    vesselDualFuelHeading,
];

export default headers;
